import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTutorial } from "../hooks/useTutorial";
import TutorialOverlay from "../components/TutorialOverlay";
import "./SeatingScreen.css";

const HEAD_TABLE = [
  "Świadek Szymon",
  "Pan Młody",
  "Panna Młoda",
  "Świadkowa Karo",
];

const TABLES = [
  {
    label: "Stół 1",
    columns: [
      [
        "Ignaś",
        "Marianna Graboś",
        "Henryk Graboś",
        "Zbigniew Siedlec",
        "Agata Graboś",
        "Bożena Turska",
        "Ola",
        "Bartek Turski",
        "Paweł Turski",
        "Anna Kazimierska",
        "Osoba Towarzysząca",
        "Marta Kowalska-Horoń",
        "Łukasz Kowalski",
        "Agata Hoffmann",
        "Patryk Kozłowski",
      ],
      [
        "Karolina Teodorowicz",
        "Maria Siedlec",
        "Jan Siedlec",
        "Ewa Pawłowska",
        "Sabina Paszkowska",
        "Nina Szamow",
        "Wiaczesław Szamow",
        "Kasia Szamow-Badowska",
        "Nadia",
        "Namir",
        "Przemysław Badowski",
        "Michał Snitko-Pleszko",
        "Ewa Smosarska",
        "Maria Kozłowska",
        "Aleksandra Kozłowska",
      ],
    ],
  },
  {
    label: "Stół 2",
    columns: [
      [
        "Mateusz Pawłowski",
        "Ola Kwiatkowska",
        "Adam Zych",
        "Werka Zych",
        "Diana Guzek",
        "Tomek Guzek",
        "Kasia Pizoń",
        "Grzesiek Pizoń",
        "Michał Jeleń",
        "Julia Fil",
        "Weronika Bittner",
        "Filip Bittner",
        "Ania Wojnowska-Wiercioch",
        "Zuzia",
        "Paweł Wiercioch",
      ],
      [
        "Paweł Kryński",
        "Aneta Wachowicz",
        "Witek Kwaśnik",
        "Ewka Kwaśnik",
        "Mateusz Gorzelany",
        "Vincent Dublański",
        "Michalina Gmaj",
        "Joanna Królikowska",
        "Michał Zub",
        "Piotr Wasilewski",
        "Marcin Jóźwiak",
        "Janina Jóźwiak",
        "Julia Nikiel",
        "Miśka",
        "Mariusz Nikiel",
      ],
    ],
  },
];

const TUTORIAL_STEPS = [
  { icon: "🔍", text: "Wpisz swoje <strong>imię lub nazwisko</strong>, aby znaleźć swoje miejsce." },
  { icon: "👆", text: "Przesuń w lewo lub w prawo, aby przełączać między <strong>Stołem 1</strong> i <strong>Stołem 2</strong>." },
  { icon: "✨", text: "Po wpisaniu imienia zostaniesz <strong>automatycznie przeniesiony</strong> do właściwego stołu." },
];

function isMatch(name, query) {
  if (!query.trim()) return false;
  return name.toLowerCase().includes(query.toLowerCase());
}

export default function SeatingScreen() {
  const [query, setQuery] = useState("");
  const [activeSlide, setActiveSlide] = useState(0);
  const tutorial = useTutorial("seating");
  const sliderRef = useRef(null);
  const isSearching = query.trim().length > 0;

  // Scroll slider to a specific slide index
  const goToSlide = useCallback((index) => {
    if (!sliderRef.current) return;
    sliderRef.current.scrollTo({
      left: index * sliderRef.current.offsetWidth,
      behavior: "smooth",
    });
    setActiveSlide(index);
  }, []);

  // Track active slide on manual swipe
  const handleScroll = useCallback(() => {
    if (!sliderRef.current) return;
    const { scrollLeft, offsetWidth } = sliderRef.current;
    setActiveSlide(Math.round(scrollLeft / offsetWidth));
  }, []);

  // On search: switch to the right slide, then scroll to match
  useEffect(() => {
    if (!isSearching) return;

    // Find which table has the match
    const matchIndex = TABLES.findIndex((table) =>
      [...table.columns[0], ...table.columns[1]].some((n) => isMatch(n, query))
    );

    if (matchIndex !== -1 && matchIndex !== activeSlide) {
      goToSlide(matchIndex);
    }

    // Scroll to matched name after slide settles
    const timer = setTimeout(() => {
      const el = sliderRef.current?.querySelector(".seating__name--match");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 380);

    return () => clearTimeout(timer);
  }, [query]); // goToSlide and activeSlide intentionally omitted

  return (
    <div className="screen">
      <div className="page-header">
        <h1 className="page-header__title">Rozsadzenie</h1>
        <p className="page-header__sub">Plan stołów</p>
        <button className="help-btn" onClick={tutorial.reopen} title="Pomoc">?</button>
      </div>

      <div className="screen__body">

        {/* Search */}
        <div className="seating__search-wrap">
          <span className="seating__search-icon">🔍</span>
          <input
            className="seating__search"
            type="text"
            placeholder="Wpisz swoje imię..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button className="seating__search-clear" onClick={() => setQuery("")}>✕</button>
          )}
        </div>

        {/* Head table — single row */}
        <div className="seating__head-card">
          <div className="seating__head-bar" />
          <div className="seating__head-label">Stół Główny</div>
          <div className="seating__head-row">
            {HEAD_TABLE.map((name, i) => (
              <React.Fragment key={name}>
                <span
                  className={`seating__head-name
                    ${isMatch(name, query) ? "seating__name--match" : ""}
                    ${isSearching && !isMatch(name, query) ? "seating__name--dim" : ""}
                  `}
                >
                  {name}
                </span>
                {i < HEAD_TABLE.length - 1 && (
                  <span className="seating__head-sep">·</span>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Slide indicators */}
        <div className="seating__dots">
          {TABLES.map((t, i) => (
            <button
              key={i}
              className={`seating__dot ${activeSlide === i ? "seating__dot--active" : ""}`}
              onClick={() => goToSlide(i)}
              aria-label={t.label}
            >
              <span className="seating__dot-label">{t.label}</span>
            </button>
          ))}
        </div>

        {/* Swipeable table slides */}
        <div
          className="seating__slider"
          ref={sliderRef}
          onScroll={handleScroll}
        >
          {TABLES.map((table) => {
            const [left, right] = table.columns;
            return (
              <div key={table.label} className="seating__slide">
                <div className="seating__table-visual">
                  {/* Left column */}
                  <div className="seating__col seating__col--left">
                    {left.map((name) => (
                      <div
                        key={name}
                        className={`seating__name seating__name--left
                          ${isMatch(name, query) ? "seating__name--match" : ""}
                          ${isSearching && !isMatch(name, query) ? "seating__name--dim" : ""}
                        `}
                      >
                        {name}
                      </div>
                    ))}
                  </div>

                  {/* Table bar */}
                  <div className="seating__table-bar">
                    <span className="seating__table-bar-label">{table.label}</span>
                  </div>

                  {/* Right column */}
                  <div className="seating__col seating__col--right">
                    {right.map((name) => (
                      <div
                        key={name}
                        className={`seating__name seating__name--right
                          ${isMatch(name, query) ? "seating__name--match" : ""}
                          ${isSearching && !isMatch(name, query) ? "seating__name--dim" : ""}
                        `}
                      >
                        {name}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ height: 24 }} />
      </div>

      {tutorial.visible && (
        <TutorialOverlay
          title="Rozsadzenie gości 🪑"
          steps={TUTORIAL_STEPS}
          onClose={tutorial.dismiss}
        />
      )}
    </div>
  );
}
